import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { Tabs, TabsList, TabsTrigger, TypographyPageTitle } from '@seen/ui'
import { isEmpty } from 'lodash-es'
import { type JSX, useMemo } from 'react'
import { Loader } from '#src/components/core/Loader.tsx'
import { TaskList } from '#src/components/task/list/TaskList.tsx'
import { useCurrentParticipant } from '#src/contexts/current-participant/useCurrentParticipant.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import {
  TasksViewValue,
  useParticipantPageStore,
} from '#src/pages/participants/timeline/store.ts'

export function ParticipantTasksPage(): JSX.Element {
  const currentParticipant = useCurrentParticipant()
  const currentPractitioner = useCurrentPractitioner()

  const view = useParticipantPageStore((state) => state.tasks.view)
  const setView = useParticipantPageStore((state) => state.setTasksView)

  const { data: allTasks = [], isLoading } = useSearch(
    StandardTask,
    StandardTask.byParticipant(currentParticipant.reference)
  )

  const assignedToMeTasks = useMemo(
    () =>
      allTasks.filter(
        (task) =>
          task.ownerPractitioner?.reference ===
          currentPractitioner.reference.reference
      ),
    [allTasks, currentPractitioner.reference.reference]
  )

  const activeAllTasksCount = useMemo(
    () => allTasks.filter((task) => !task.isCompletedOrCancelled).length,
    [allTasks]
  )

  const activeAssignedToMeTasksCount = useMemo(
    () =>
      assignedToMeTasks.filter((task) => !task.isCompletedOrCancelled).length,
    [assignedToMeTasks]
  )

  const displayedTasks =
    view === TasksViewValue.AssignedToMe ? assignedToMeTasks : allTasks

  return (
    <div className="flex flex-col gap-6">
      <TypographyPageTitle>Tasks</TypographyPageTitle>
      <Tabs value={view}>
        <TabsList>
          <TabsTrigger
            value={TasksViewValue.All}
            onClick={() => setView(TasksViewValue.All)}
          >
            All tasks ({activeAllTasksCount})
          </TabsTrigger>
          <TabsTrigger
            value={TasksViewValue.AssignedToMe}
            onClick={() => setView(TasksViewValue.AssignedToMe)}
          >
            Assigned to me ({activeAssignedToMeTasksCount})
          </TabsTrigger>
        </TabsList>
      </Tabs>
      {isLoading && <Loader />}
      {!isLoading && isEmpty(displayedTasks) && (
        <p className="text-muted-foreground">
          {view === TasksViewValue.AssignedToMe
            ? `You have no tasks assigned for ${currentParticipant.name}.`
            : `There are no tasks for ${currentParticipant.name}.`}
        </p>
      )}
      {!isLoading && !isEmpty(displayedTasks) && (
        <div className="-mx-pagex-default lg:-mx-pagex-lg">
          <TaskList
            tasks={displayedTasks}
            participant={currentParticipant.reference}
          />
        </div>
      )}
    </div>
  )
}
